import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import extractUrls from "extract-urls";
import {
  Box,
  Flex,
  Text,
  Link,
  Tooltip,
  Icon,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Spinner,
  ModalHeader, ModalFooter,
} from "@chakra-ui/react";
import {
  ExternalLinkIcon,
  CheckCircleIcon,
  EmailIcon,
  ChatIcon,
  PhoneIcon,
} from "@chakra-ui/icons";
import { RiRecordMailFill } from "react-icons/ri"
import { cqAPI, HOST } from "../../lib/Auth";
import { faGlobe, faPlayCircle, faCalendarCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SignDocument from "../SignDocument/SignDocument"; // Import the SignDocument component
import moment from "moment";
import { RiSparkling2Fill, RiStickyNoteLine } from "react-icons/ri";

const internalNoteTaskLabel = 'This message is for internal use only and was not sent to the patient.';

const removeTables = (htmlString) => {
  if (!htmlString || !/<table[\s>]/i.test(htmlString)) return htmlString || '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const tables = doc.querySelectorAll("table");
  tables.forEach((table) => {
    const parent = table.parentNode;
    while (table.firstChild) {
      parent.insertBefore(table.firstChild, table);
    }
    parent.removeChild(table);
  });
  return doc.body.innerHTML;
};

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const hyperlinkUrls = (text, isUserMessage) => {
  if (!text) return "";

  // Decode &amp; → & so the URL regex captures full query-strings
  // (removeTables encodes & as &amp; via innerHTML; DOMPurify will
  //  re-encode when it sanitises the final result)
  const decoded = text.replace(/&amp;/g, '&');

  // Shelve existing <a>…</a> blocks so their content isn't re-linkified
  const anchorPhs = new Map();
  let anchorIdx = 0;
  let out = decoded.replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, (m) => {
    const key = ` __ANCHOR__${anchorIdx++} `;
    anchorPhs.set(key, m);
    return key;
  });

  // Shelve remaining HTML tags so URLs in attributes (e.g. <img src="…">)
  // aren't picked up by extractUrls
  const tagPhs = new Map();
  let tagIdx = 0;
  out = out.replace(/<[^>]+>/g, (m) => {
      const key = ` __TAG__${tagIdx++} `;
      tagPhs.set(key, m);
      return key;
    });

  let urls = extractUrls(out) || [];
  urls = Array.from(new Set(urls)).sort((a,b) => b.length - a.length);
  const urlPhs = new Map();

  urls.forEach((url, i) => {
    const key = `__URL__${i}`;

    const normalized = url.startsWith("http") ? url : `https://${url}`;
    let isExternal = true;
    try {
      const host = new URL(normalized).host.toLowerCase();
      isExternal = host !== window.location.host && !host.includes("calendly");
    } catch {
      isExternal = true;
    }
    const linkColor = isUserMessage ? '#3182ce' : '#86C4FF';
    const style = `color:${linkColor}; text-decoration:underline;`
    const hyperlink = `<a href="${normalized}" ${isExternal ? 'data-external-link="true"' : ""} target="_blank" rel="noopener noreferrer" style="${style}">${url}</a>`;
    urlPhs.set(key, hyperlink);
    const rawEsc = escapeRe(url);

    // A) "<url>." when followed by space or end-of-string → key + '.'
    out = out.replace(new RegExp(`${rawEsc}\\.(?=\\s|$)`, "g"), `${key}.`);
    // B) Plain "<url>" not immediately followed by a '.' (so we don't double-handle case A)
    out = out.replace(new RegExp(`${rawEsc}(?!\\.)`, "g"), key);
  });

  // Restore placeholders: URLs → tags → anchors
  urlPhs.forEach((anchor, key) => { out = out.replaceAll(key, anchor); });
  tagPhs.forEach((tag, key) => { out = out.replaceAll(key, tag); });
  anchorPhs.forEach((anchor, key) => { out = out.replaceAll(key, anchor); });

  return out;
}

const sanitizedHtml = (message, isUserMessage) => {
  const base = message.translation
    ? (message.direction === 'incoming' ? message.body : message.translation.body)
    : message.body;
  const noTables = removeTables(base);
  const withMentions = (message.mentions || []).reduce(
    (acc, cur) => acc.replace(RegExp(cur.name, "ig"), `<b>${cur.name}</b>`), noTables);

  const withHyperlinks = hyperlinkUrls(withMentions, isUserMessage);
  return DOMPurify.sanitize(withHyperlinks,{ ADD_ATTR: ["data-external-link"] });
}

function isImage(fileName) {
  if (fileName) {
    const ext = fileName.split(".").pop().toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "bmp", "tiff"].includes(ext);
  }
  return false;
}

function Messages({
  messages,
  disableSendButton,
  userInfo,
  colorMode,
  isPatient,
  fetchAllMessages,
  setMessages,
  isMobile,
  hasMore,
  onLoadMore,
  isLoadingMore,
  page
}) {
  const bottomOfList = useRef(null);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();
  const { uid } = useParams();
  const [selectedSignatureRequestId, setSelectedSignatureRequestId] =
    useState(null);
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [attachmentBlobUrls, setAttachmentBlobUrls] = useState({});
  const [pendingLink, setPendingLink] = useState(null);
  const [cancelRetryBooking, setCancelRetryBooking] = useState(null);

  const scrollToBottom = useCallback(() => {
    bottomOfList.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });
  }, []);

  // Scroll to bottom only on initial load (page=0) or when new messages arrive at the bottom.
  useEffect(() => {
    if (page === 0 && messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom, page]);

  useEffect(() => {
    if (messages.length > 0) {
      const timeout = setTimeout(() => {
        if (page === 0) {
          scrollToBottom();
        }
      }, 400);
      return () => clearTimeout(timeout);
    }
  }, [attachmentBlobUrls, scrollToBottom, messages, page]);

  useEffect(() => {
    const handleClick = (e) => {
      const anchor = e.target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      const url = new URL(href, window.location.origin);
      if (url.pathname === "/cancel-retry-booking") {
        e.preventDefault();
        const search = url.search || "";
        const pathWithQuery = `${url.pathname}${search}`;
        cqAPI
          .get(pathWithQuery)
          .then((res) => {
            if (res?.code === 1100) {
              // Needs confirmation: open modal and store context
              setCancelRetryBooking({
                href: pathWithQuery,
                msg: res.msg,
                data: res.data || {},
              });
            }
          })
          return;
        }

      // External link handling
      const link = e.target.closest("a[data-external-link]");
      if (link) {
        e.preventDefault();
        setPendingLink(link.getAttribute("href"));
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const fetchBlobUrl = async (attachmentUrl) => {
    try {
      const downloadUrl = await cqAPI.downloadFile(attachmentUrl);
      if (!downloadUrl) {
        console.error("File unavailable for download:", attachmentUrl);
        return null;
      }

      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      return blobUrl;
    } catch (error) {
      console.error("Failed to fetch file:", error);
      return null;
    }
  };

  useEffect(() => {
    if (!messages.length) return;
    let isCancelled = false;

    const loadAll = async () => {
      const newUrls = {};
      await Promise.all(
        messages.flatMap(msg =>
          (msg.attachments || [])
            .filter(att => (isImage(att.name) || att.name === 'Voicemail') && !attachmentBlobUrls[att.url])
            .map(async att => {
              try {
                const blobUrl = await fetchBlobUrl(att.url);
                newUrls[att.url] = blobUrl;
              } catch (e) {
                console.error('Error loading attachment', e);
              }
            })
        )
      );
      if (!isCancelled && Object.keys(newUrls).length) {
        setAttachmentBlobUrls(prev => ({ ...prev, ...newUrls }));
      }
    };

    loadAll();
    return () => { isCancelled = true; };
  }, [messages]);


  const cancelPaymentRequest = async (message_id) => {
    const response = await cqAPI.get(
      `/payments/request/cancel?cid=${message_id}`
    );
    if (response.type === "ok") {
      setMessages(
        messages.map((m) => {
          if (m._id === message_id) {
            m.payrequest.cancelled = new Date();
          }
          return m;
        })
      );
    }
  };

  const handleSignModalClose = () => {
    setIsSignModalOpen(false);
    setSelectedSignatureRequestId(null);
  };

  const handleSign = (signatureRequestId) => {
    setSelectedSignatureRequestId(signatureRequestId);
    setIsSignModalOpen(true);
  };

  const handleSignatureSuccess = async () => {
    setIsSignModalOpen(false);
    setSelectedSignatureRequestId(null);
    await fetchAllMessages();
  };

  const cancelSignatureRequest = async (message_id, request_id) => {
    const response = await cqAPI.del(
      `/forms?_uid=${uid}&request_id=${request_id}`
    );
    if (response.type === "ok") {
      setMessages(
        messages.map((m) => {
          if (m._id === message_id) {
            m.formrequest.status = "cancelled";
          }
          return m;
        })
      );
    }
  };

  const downloadFile = async (attachmentUrl, fileName) => {
    try {
      const downloadUrl = await cqAPI.downloadFile(attachmentUrl);
      if (!downloadUrl) {
        console.error("File unavailable for download:", attachmentUrl);
        return null;
      }

      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);

      return blobUrl;
    } catch (error) {
      console.error("Failed to download file:", error);
      return null;
    }
  };

  // SS-4054: the AI no longer produces drafts. Historical ones are still in the
  // collection and the API no longer returns them, so this is belt and braces.
  const displayableMessages = () => messages.filter((m) => m.type !== "draft");

  const getMessageIconAndLabel = (type, ai_generated, direction, source) => {
    let icons = [];
    if (ai_generated || source?.startsWith('vam') || source === 'vav') {
      icons.push({ icon: RiSparkling2Fill, label: "AI" });
    }
    if (direction === 'internal_note') {
      icons.push({icon: RiStickyNoteLine, label: internalNoteTaskLabel})
    } else {
      switch (type) {
        case "call":
          icons.push({icon: PhoneIcon, label: "Phone Call"});
          break;
        case "email":
          icons.push({icon: EmailIcon, label: "Email"});
          break;
        case "sms":
          icons.push({icon: ChatIcon, label: "SMS"});
          break;
        case "emailsms":
          icons.push({icon: EmailIcon, label: "Email"});
          icons.push({icon: ChatIcon, label: "SMS"});
          break;
        case "voicemail":
          icons.push({icon: RiRecordMailFill, label: "Voicemail"});
          break;
        case "api":
          !ai_generated && !source && icons.push({as: faGlobe, label: "Website Chat"});
          break;
        default:
          break;
      }
      switch (source) {
        case "vav":
          icons.push({icon: PhoneIcon, label: "Phone Call"});
          break;
        case "ezra-voice":
          icons.push({icon: PhoneIcon, label: "Phone Call"});
          break;
        case "vam-email":
          icons.push({icon: EmailIcon, label: "Email"});
          break;
        case "ezra-email":
          icons.push({icon: EmailIcon, label: "Email"});
          break;
        case "vam-sms":
          icons.push({icon: ChatIcon, label: "SMS"});
          break;
        case "ezra-sms":
          icons.push({icon: ChatIcon, label: "SMS"});
          break;
        case "vam-api":
          icons.push({as: faGlobe, label: "Website Chat"})
          break;
        case "ezra-api":
          icons.push({as: faGlobe, label: "Website Chat"})
          break;
        case "pp":
        case "pp-agent":
        case "booking-app":
          icons.push({as: faCalendarCheck, label: "Online Booking"});
          break;
        default:
          break;
      }
    }
    return icons;
  };

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // If user scrolled to the top and we have more messages, load more
    if (container.scrollTop <= 0 && hasMore && !isLoadingMore) {
      const oldScrollHeight = container.scrollHeight;
      const oldScrollTop = container.scrollTop;
      onLoadMore(oldScrollHeight, oldScrollTop, container);
    }
  }, [hasMore, onLoadMore, isLoadingMore]);
  const [showTranslation, setShowTranslation] = useState({});

  return (
    <div
      className="message-list"
      ref={scrollContainerRef}
      onScroll={handleScroll}
      style={{ overflowY: "auto", position: "relative" }}
    >
      {/* Top loader indicator */}
      {isLoadingMore && (
        <Flex
          position="absolute"
          top="0"
          width="100%"
          justifyContent="center"
          alignItems="center"
          background="rgba(255,255,255,0.8)"
          zIndex={10}
          py={2}
        >
          <Spinner size="sm" />
        </Flex>
      )}

      <div
        style={{ maxWidth: "100%", paddingTop: isLoadingMore ? "24px" : "0" }}
      >
        {messages.length === 0 ? (
          <>
            {disableSendButton && (
              <div
                className="spinner"
                style={{ position: "absolute", bottom: "0px" }}
              ></div>
            )}
          </>
        ) : (
          displayableMessages().map((message, index) => {
            const isUserMessage =
              message.author === userInfo.user_id ||
              (message.type === "call" && message.direction === "incoming");
            const seen = !!message?.emailStatus?.status?.opened;
            const bgColor =
              message.direction === "internal_note"
                ? "orange.100"
                : isUserMessage
                ? "#E2F3F3"
                : colorMode === "dark"
                ? "#2C2F40"
                : "#64728C";

            const messageColor =
              message.direction === "internal_note"
                ? "#000"
                : isUserMessage
                ? "#092728"
                : colorMode === "dark"
                ? "#FFFFFF"
                : "#F9F9FB";
            const footerColor =
              message.direction === "internal_note"
                ? "#000"
                : isUserMessage
                ? "#092728"
                : colorMode === "dark"
                ? "#FFFFFF"
                : "#F9F9FB";
            const messageBorderRadius = !isUserMessage
              ? "16px 0px 16px 16px"
              : "0px 16px 16px 16px";
            let messageMaxWidth = isUserMessage ? "450px" : "350px";
            if (isMobile) {
              messageMaxWidth = isUserMessage ? "320px" : "300px";
            }

            return (
              <Flex
                key={index}
                direction="column"
                style={{ overflowX: "hidden" }}
                align={isUserMessage ? "flex-start" : "flex-end"}
                my={2}
                color={messageColor}
              >
                <Box
                  p={3}
                  bg={bgColor}
                  borderRadius={messageBorderRadius}
                  position="relative"
                  maxWidth={messageMaxWidth}
                  minWidth="200px"
                  className="inbox-message"
                >
                  <Text
                    as="p"
                    fontSize="md"
                    align="left"
                    paddingBottom="20px"
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml(message, isUserMessage) }}
                  />
                    {message.translation && (
                      <>
                        <Button
                          variant="link"
                          colorScheme="black"
                          paddingLeft="0"
                          onClick={() =>
                            setShowTranslation((prev) => ({
                              ...prev,
                              [message._id]: !prev[message._id],
                            }))
                          }
                        >
                          {showTranslation[message._id] ? "Hide translation" : (message.direction === 'incoming'? "See the translation" : `Sent in ${userInfo.language?.name || 'unknown language'}`)}
                        </Button>
                        {showTranslation[message._id] && (
                          <Text
                            as="p"
                            fontSize="md"
                            align="left"
                            paddingBottom="20px"
                            marginLeft="0"
                          >
                            {message.direction === 'incoming'? message.translation.body : message.body}
                          </Text>
                        )}
                      </>
                    )}
                  
                  {message.attachments && message.attachments.length > 0 && (
                    <Flex direction="column">
                      {message.attachments.map((attachment) => (
                        <React.Fragment key={attachment.url}>
                          {isImage(attachment.name) ? (
                            <Link
                              onClick={(e) => {
                                e.preventDefault();
                                const blobUrl =
                                  attachmentBlobUrls[attachment.url];
                                if (blobUrl) {
                                  window.open(blobUrl, "_blank");
                                }
                              }}
                              href="#"
                            >
                              {attachmentBlobUrls[attachment.url] ? (
                                <img
                                  src={attachmentBlobUrls[attachment.url]}
                                  alt={attachment.name}
                                  style={{
                                    maxWidth: "100%",
                                    marginBottom: "10px",
                                  }}
                                />
                              ) : (
                                <Text>Loading...</Text>
                              )}
                            </Link>
                          ) : (
                            attachment.name === "Voicemail" ? (
                                <Flex align="center" mb={4} width="100%">
                                  <Box flex="1">
                                    <audio controls style={{ width: "100%" }} key={attachmentBlobUrls[attachment.url] || 'loading'}>
                                      <source src={attachmentBlobUrls[attachment.url]} />
                                      Your browser does not support the audio element.
                                    </audio>
                                  </Box>
                                </Flex>
                              ) : (
                                <Link
                                  onClick={(e) => {
                                    e.preventDefault();
                                    downloadFile(attachment.url, attachment.name);
                                  }}
                                  href="#"
                                  target="_blank"
                                >
                                  {attachment.name}
                                  <ExternalLinkIcon ml="2" />
                                </Link>
                              )
                            )
                          }
                        </React.Fragment>
                      ))}
                    </Flex>
                  )}
                  {message.payrequest &&
                    !message.payrequest.cancelled &&
                    !isPatient &&
                    !message.payrequest.paid && (
                      <Box className="payrequest" style={{ maxWidth: "100%" }}>
                        <Box
                          style={{
                            marginBottom: "20px",
                            color: messageColor,
                          }}
                        >
                          Description: {message.payrequest.description}
                        </Box>
                        <Box
                          style={{
                            marginBottom: "20px",
                            color: messageColor,
                          }}
                        >
                          Amount: $
                          {(message.payrequest.amount / 100).toFixed(2)}
                        </Box>
                        <Box
                          style={{
                            textAlign: "right",
                            marginBottom: "20px",
                          }}
                        >
                          <Button
                            bg="red.500"
                            color="white"
                            _hover={{ bg: "red.600" }}
                            onClick={() => cancelPaymentRequest(message._id)}
                          >
                            Cancel Payment Request
                          </Button>
                        </Box>
                      </Box>
                    )}
                  {message.payrequest &&
                    !message.payrequest.cancelled &&
                    isPatient &&
                    !message.payrequest.paid && (
                      <Box className="payrequest" style={{ maxWidth: "100%" }}>
                        <Box
                          style={{
                            marginBottom: "20px",
                            color: messageColor,
                          }}
                        >
                          Description: {message.payrequest.description}
                        </Box>
                        <Box
                          style={{
                            marginBottom: "20px",
                            color: messageColor,
                          }}
                        >
                          Amount: $
                          {Math.round(message.payrequest.amount / 100).toFixed(
                            2
                          )}
                        </Box>
                        <Box
                          style={{
                            textAlign: "right",
                            marginBottom: "20px",
                          }}
                        >
                          <Button>
                            <Link
                              target="_blank"
                              href={`${HOST}/payments/request/pay?bid=${message.payrequest.bid}&cid=${message._id}&_uid=${message.uid}`}
                            >
                              Make the Payment
                            </Link>
                          </Button>
                        </Box>
                      </Box>
                    )}
                  {message.formrequest &&
                    message.formrequest.status === "awaiting_signature" &&
                    isPatient &&
                    !message.formrequest.signed && (
                      <Box className="payrequest" style={{ maxWidth: "100%" }}>
                        <Box
                          style={{
                            marginBottom: "20px",
                            color: messageColor,
                          }}
                        >
                          Description: {message.formrequest.title}
                        </Box>
                        <Box
                          style={{
                            textAlign: "right",
                            marginBottom: "20px",
                          }}
                        >
                          <Button
                            onClick={() =>
                              handleSign(message.formrequest.request_id)
                            }
                          >
                            Sign
                          </Button>
                        </Box>
                      </Box>
                    )}
                    {message.formrequest &&
                      message.formrequest.attachistory &&
                      isPatient &&
                      !message.formrequest.signed && (
                        <Box className="payrequest" style={{ maxWidth: "100%" }}>
                          <Box
                            style={{
                              marginBottom: "20px",
                              color: messageColor,
                            }}
                          >
                            Description: {message.formrequest.title}
                          </Box>
                          <Box
                            style={{
                              textAlign: "right",
                              marginBottom: "20px",
                            }}
                          >
                            <Button
                              onClick={() =>
                                {
                                  const newSearchParams = new URLSearchParams(
                                    window.location.search
                                  );
                                  navigate(`/forms?${newSearchParams.toString()}`)
                                }
                              }
                            >
                              Complete History
                            </Button>
                          </Box>
                        </Box>
                      )}
                      {message.formrequest &&
                        message.formrequest.attachistory &&
                        !isPatient &&
                        !message.formrequest.signed && (
                          <Box className="payrequest" style={{ maxWidth: "100%" }}>
                            <Box
                              style={{
                                marginBottom: "20px",
                                color: messageColor,
                              }}
                            >
                              Description: {message.formrequest.title}
                            </Box>
                            {message.formrequest.updated && (<Box
                              style={{
                                textAlign: "right",
                                marginBottom: "20px",
                              }}
                            >
                              Updated on{" "}
                              {moment(message.formrequest.updated).format(
                                "MMM D, YYYY h:mm a"
                              )}
                            </Box>)}
                          </Box>
                        )}
                  {message.formrequest &&
                    !isPatient &&
                    message.formrequest.status === "awaiting_signature" && (
                      <Box className="payrequest" style={{ maxWidth: "100%" }}>
                        <Box
                          style={{
                            marginBottom: "20px",
                            color: messageColor,
                          }}
                        >
                          Description: {message.formrequest.title}
                        </Box>
                        <Box
                          style={{
                            textAlign: "right",
                            marginBottom: "20px",
                          }}
                        >
                          <Button
                            bg="red.500"
                            color="white"
                            _hover={{ bg: "red.600" }}
                            onClick={() =>
                              cancelSignatureRequest(
                                message._id,
                                message.formrequest.request_id
                              )
                            }
                          >
                            Cancel Signature Request
                          </Button>
                        </Box>
                      </Box>
                    )}
                  {message.payrequest && message.payrequest.cancelled && (
                    <Box
                      className="payrequest"
                      style={{ maxWidth: "100%", color: messageColor }}
                    >
                      <Box style={{ marginBottom: "20px" }}>
                        {message.payrequest.description}
                      </Box>
                      <Box style={{ marginBottom: "20px" }}>
                        Amount: $
                        {Math.round(message.payrequest.amount / 100).toFixed(2)}
                      </Box>
                      <Box
                        style={{
                          textAlign: "right",
                          marginBottom: "20px",
                        }}
                      >
                        Cancelled on{" "}
                        {moment(message.payrequest.cancelled).format(
                          "MMM D, YYYY h:mm a"
                        )}
                      </Box>
                    </Box>
                  )}
                  {message.formrequest &&
                    message.formrequest.status === "cancelled" && (
                      <Box
                        className="payrequest"
                        style={{ maxWidth: "100%", color: messageColor }}
                      >
                        <Box style={{ marginBottom: "20px" }}>
                          {message.formrequest.title}
                        </Box>
                        <Box
                          style={{
                            textAlign: "right",
                            marginBottom: "20px",
                          }}
                        >
                          Cancelled on{" "}
                          {moment(message.formrequest.cancelled).format(
                            "MMM D, YYYY h:mm a"
                          )}
                        </Box>
                      </Box>
                    )}
                  {message.formrequest && message.formrequest.signed && (
                    <Box
                      className="payrequest"
                      style={{ maxWidth: "100%", color: messageColor }}
                    >
                      <Box style={{ marginBottom: "20px" }}>
                        {message.formrequest.title}
                      </Box>
                      <Box
                        style={{
                          textAlign: "right",
                          marginBottom: "20px",
                        }}
                      >
                        Signed on{" "}
                        {moment(message.formrequest.signed).format(
                          "MMM D, YYYY h:mm a"
                        )}
                      </Box>
                    </Box>
                  )}
                  {message.payrequest &&
                    !message.payrequest.cancelled &&
                    message.payrequest.paid && (
                      <Box
                        className="payrequest"
                        style={{ maxWidth: "100%", color: messageColor }}
                      >
                        <Box style={{ marginBottom: "20px" }}>
                          {message.payrequest.description}
                        </Box>
                        <Box style={{ marginBottom: "20px" }}>
                          Amount: $
                          {Math.round(message.payrequest.amount / 100).toFixed(
                            2
                          )}
                        </Box>
                        <Box
                          style={{
                            textAlign: "right",
                            marginBottom: "20px",
                          }}
                        >
                          Paid on{" "}
                          {moment(message.payrequest.paid).format(
                            "MMM D, YYYY h:mm a"
                          )}
                        </Box>
                      </Box>
                    )}

                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color={footerColor} mt={2}>
                      {(message.author_name || "").replace(/[^a-zA-Z ]/g, "")}
                    </Text>
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color={footerColor} align="left">
                      {moment(message.created).format("MMM D, YYYY h:mm a")}
                    </Text>
                    <Flex align="center">
                      {
                        getMessageIconAndLabel(message.type, message.ai_generated, message.direction, message.metadata?.source).map(
                          ({ icon, label, as }, iconIndex) => (
                            <Tooltip key={iconIndex} label={label} hasArrow shouldWrapChildren>
                              {icon ? (
                                <Icon
                                  id={`${label.replace(' ', '-')}-${message._id}`}
                                  as={icon}
                                  color="#092728"
                                  className="message-icon"
                                  boxSize="32px"
                                  mr={2}
                                  verticalAlign="middle"
                                />
                              ) : (
                                <FontAwesomeIcon
                                  id={`${label.replace(' ', '-')}-${message._id}`}
                                  icon={as}
                                  className="message-icon"
                                  size="1x"
                                  style={{
                                    marginRight: "8px",
                                    verticalAlign: "middle",
                                    display: "inline-block"
                                  }}
                                />
                              )}
                            </Tooltip>
                          )
                        )
                      }
                      {message.type === "call" && !isPatient && (
                        <Flex
                          _hover={{ cursor: "pointer" }}
                          onClick={async () => {
                            navigate(
                              `/reports/calls/${message.call_id}?bid=${message.bid}`
                            );
                          }}
                        >
                          <FontAwesomeIcon
                            id={`play-${message._id}`}
                            icon={faPlayCircle}
                            className="message-icon"
                          />
                        </Flex>
                      )}
                      {message.type === "voicemail" && !isPatient && (
                        <Flex
                          _hover={{ cursor: "pointer" }}
                          onClick={async () => {
                            navigate(
                              `/reports/calls/${message.call_id_obj}?bid=${message.bid}`
                            );
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faPlayCircle}
                            className="message-icon"
                          />
                        </Flex>
                      )}
                      {seen && (
                        <Tooltip label="Message was opened" hasArrow>
                          <Icon
                            as={CheckCircleIcon}
                            color={footerColor}
                            w={3}
                            h={3}
                          />
                        </Tooltip>
                      )}
                    </Flex>
                  </Flex>
                </Box>
              </Flex>
            );
          })
        )}
        <div ref={bottomOfList} />
      </div>
      <Modal
        isOpen={isSignModalOpen}
        onClose={handleSignModalClose}
        size="full"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <SignDocument
              signatureRequestId={selectedSignatureRequestId}
              onSuccess={handleSignatureSuccess}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={!!pendingLink}
        onClose={() => setPendingLink(null)}
      >
        <ModalOverlay/>
        <ModalContent>
          <ModalCloseButton onClick={() => setPendingLink(null)}/>
          <ModalHeader>External Link</ModalHeader>
          <ModalBody>
            This link will open an external site.
            <br/>
            <b>{pendingLink}</b>
            <br/>
            Would you like to proceed?
          </ModalBody>
          <ModalFooter mb={2}>
            <Button id={"cancel-link"} variant={"ghost"} onClick={() => setPendingLink(null)} mr={2}>Cancel</Button>
            <Button id={"open-link"} mr={2} onClick={() => {
              if (pendingLink) {
                console.log(pendingLink)
                window.open(pendingLink, "_blank", "noopener, noreferrer");
              }
              setPendingLink(null);
            }}>Open</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!cancelRetryBooking} onClose={() => setCancelRetryBooking(null)} >
        <ModalOverlay/>
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Cancel Auto-Repair</ModalHeader>
          <ModalBody>
            {cancelRetryBooking?.msg || (
              "If you cancel the auto-repair, the AI will stop trying to fix the issue. A new task will be created for your team to manually book the appointment."
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}

export default Messages;
